import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
} from "@mui/material";
import * as React from "react";

import { SkillSummary, SkillVersionRecord } from "@/shared/domain/skills";
import { useSkillVersions } from "@/shared/domain/skills/hooks";
import { Button } from "@/shared/foundation/primitives";

import styles from "./SkillInspector.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The skill being inspected. Read-only: subscribers don't get to mutate. */
  summary: SkillSummary | null;
  /** Specific version to pre-select when opening. Defaults to the latest
   * (MAX(version)) version. Use to deep-link from the version sub-table's
   * Inspect button. */
  initialVersion?: number | null;
}

function latestVersion(
  versions: SkillVersionRecord[] | undefined,
): SkillVersionRecord | null {
  if (!versions || versions.length === 0) {
    return null;
  }
  return versions.reduce((a, b) => (a.version > b.version ? a : b));
}

/**
 * Read-only viewer for skills the caller doesn't author.
 *
 * Subscribers see the full body of any version of the skill but cannot edit,
 * add versions, or delete. The version picker defaults to the latest version
 * but lets the user browse older bodies for context.
 */
export function SkillInspector({
  open,
  onClose,
  summary,
  initialVersion,
}: Props) {
  const versionsQuery = useSkillVersions(
    open ? summary?.skill.skill_id : undefined,
  );
  const [selectedVersion, setSelectedVersion] = React.useState<number | null>(
    null,
  );

  // Default the picker to the explicit ``initialVersion`` when one is passed
  // (so the per-version Inspect button deep-links into the right body),
  // otherwise to the live version. Reseed on every (re)open so a stale
  // selection from a previous open doesn't mis-render.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedVersion(
      initialVersion ?? summary?.latest_version?.version ?? null,
    );
  }, [
    open,
    summary?.skill.skill_id,
    summary?.latest_version?.version,
    initialVersion,
  ]);

  const versions = versionsQuery.data ?? [];
  const chosen =
    versions.find((v) => v.version === selectedVersion) ??
    latestVersion(versions);

  const title = summary
    ? `${summary.skill.name}${chosen ? ` (v${chosen.version})` : ""}`
    : "Skill";

  let bodyContent: React.ReactNode;
  if (versionsQuery.isLoading) {
    bodyContent = <CircularProgress size={20} />;
  } else if (versionsQuery.isError) {
    bodyContent = (
      <p>Couldn&apos;t load versions. Try closing and re-opening.</p>
    );
  } else if (chosen) {
    bodyContent = (
      <>
        <div>
          <strong>When to use</strong>
          <p>{chosen.description}</p>
        </div>
        <div>
          <strong>Procedure</strong>
          <pre className={styles.body}>{chosen.body}</pre>
        </div>
      </>
    );
  } else {
    // Defense-in-depth: every skill has ≥1 version as an invariant.
    // Reaching this branch implies a future bug, not a live state.
    bodyContent = <p>No versions to inspect.</p>;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={styles.formStack}>
          {versions.length > 1 && (
            <Select
              size="small"
              value={chosen?.version ?? ""}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
            >
              {versions.map((v) => (
                <MenuItem key={v.version} value={v.version}>
                  v{v.version}
                </MenuItem>
              ))}
            </Select>
          )}
          {bodyContent}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
